import Header from "@/components/hotel/Header";
import SubpageHero from "@/components/hotel/SubpageHero";
import Footer from "@/components/hotel/Footer";
import SEO from "@/components/SEO";
import {
  Building, Mail, Phone, Globe, FileText, Code,
  ScrollText, Handshake, XCircle, Users, DoorOpen,
  ShieldCheck, Scale, Gift
} from "lucide-react";

const ImpressumPage = () => {
  return (
    <main id="main-content" className="min-h-screen bg-background">
      <SEO
        title="Impressum & AGB"
        description="Impressum, Allgemeine Geschäftsbedingungen und rechtliche Informationen des ESOS Hotel Quelle, Bad Ragaz."
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Impressum", path: "/impressum" },
        ]}
      />
      <Header />
      <SubpageHero title="Impressum" subtitle="Angaben & AGB" />

      <section className="hotel-section bg-card">
        <div className="hotel-container max-w-4xl">

          {/* Company Info */}
          <div className="bg-background p-8 rounded-lg shadow-soft mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                <Building className="w-5 h-5 text-accent" />
              </div>
              <h2 className="font-display text-2xl font-semibold text-foreground">
                Unternehmensinformationen
              </h2>
            </div>
            <div className="space-y-2 font-body text-muted-foreground">
              <p className="font-semibold text-foreground">Esos Hotel Quelle</p>
              <p>Handelsregister: Kanton St. Gallen</p>
              <p>UID-Nummer: CHE-114.450.031 MwSt</p>
            </div>
          </div>

          {/* Contact */}
          <div className="bg-background p-8 rounded-lg shadow-soft mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                <Mail className="w-5 h-5 text-accent" />
              </div>
              <h2 className="font-display text-2xl font-semibold text-foreground">
                Kontaktinformationen
              </h2>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-1 font-body text-muted-foreground">
                <p className="font-semibold text-foreground mb-2">Adresse</p>
                <p>Badstrasse 9a</p>
                <p>7310 Bad Ragaz</p>
                <p>St. Gallen, Schweiz</p>
              </div>
              <div className="space-y-3 font-body text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-accent shrink-0" />
                  <span>+41 81 302 11 13</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-accent shrink-0" />
                  <span>info@esoshotelquelle.com</span>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-accent shrink-0" />
                  <span>www.esoshotelquelle.com</span>
                </div>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-background p-8 rounded-lg shadow-soft mb-6">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                <FileText className="w-5 h-5 text-accent" />
              </div>
              <h2 className="font-display text-2xl font-semibold text-foreground">
                Haftungsausschluss
              </h2>
            </div>
            <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Haftung für Inhalte</h3>
                <p>
                  Die Inhalte unserer Seiten wurden mit grösster Sorgfalt erstellt. Für die Richtigkeit,
                  Vollständigkeit und Aktualität der Inhalte können wir jedoch keine Gewähr übernehmen.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Haftung für Links</h3>
                <p>
                  Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen
                  Einfluss haben. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter
                  verantwortlich.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Urheberrecht</h3>
                <p>
                  Die durch die Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen
                  dem Schweizer Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art
                  der Verwertung bedürfen der schriftlichen Zustimmung des jeweiligen Autors.
                </p>
              </div>
            </div>
          </div>

          {/* Technical */}
          <div className="bg-background p-8 rounded-lg shadow-soft mb-16">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                <Code className="w-5 h-5 text-accent" />
              </div>
              <h2 className="font-display text-2xl font-semibold text-foreground">
                Technische Umsetzung
              </h2>
            </div>
            <div className="space-y-4 font-body text-muted-foreground">
              <p><span className="font-semibold text-foreground">Website Design & Entwicklung:</span></p>
              <div className="bg-card p-6 rounded-lg border border-border">
                <p className="font-display text-xl font-semibold text-foreground mb-2">Starkschild</p>
                <p className="text-muted-foreground mb-3">Webdesign & Digitale Lösungen</p>
                <a
                  href="https://starkschild.ch"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-accent hover:underline"
                >
                  <Globe className="w-4 h-4" />
                  www.starkschild.ch
                </a>
              </div>
              <p><span className="font-semibold text-foreground">Hosting:</span> Schweiz</p>
            </div>
          </div>

          {/* ─── AGB Section ─── */}
          <div className="text-center max-w-3xl mx-auto mb-12">
            <p className="hotel-caption text-accent mb-4">AGB</p>
            <h2 className="font-display text-4xl md:text-5xl font-light text-foreground mb-6">
              Allgemeine Geschäftsbedingungen
            </h2>
            <p className="hotel-body text-muted-foreground">
              General Terms and Conditions (GTC) des Esos Hotel Quelle, Bad Ragaz
            </p>
          </div>

          <div className="space-y-6">
            {/* 1. Scope */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <ScrollText className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  1. Geltungsbereich
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>
                  Diese Allgemeinen Geschäftsbedingungen gelten für Verträge über die Vermietung
                  von Hotelzimmern zu Beherbergungszwecken sowie für alle weiteren Dienstleistungen
                  und Lieferungen des Hotels an den Gast.
                </p>
                <p>
                  Die Untervermietung oder Weitervermietung der bereitgestellten Zimmer sowie deren
                  Nutzung zu anderen als Beherbergungszwecken bedürfen der vorherigen schriftlichen
                  Zustimmung des Hotels.
                </p>
                <p>
                  Die eigenen Geschäftsbedingungen des Gastes gelten nur, wenn dies ausdrücklich im
                  Voraus vereinbart wurde.
                </p>
              </div>
            </div>

            {/* 2. Contract */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Handshake className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  2. Vertragsschluss, Vertragsparteien, Haftung, Verjährung
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>
                  Der Vertrag kommt durch die Annahme der Buchungsanfrage des Gastes durch das Hotel
                  zustande. Das Hotel kann die Buchung schriftlich bestätigen.
                </p>
                <p>
                  Vertragsparteien sind das Hotel und der Gast. Bucht ein Dritter im Namen des Gastes,
                  haftet dieser gesamtschuldnerisch mit dem Gast für alle Verpflichtungen aus dem
                  Beherbergungsvertrag, sofern dem Hotel eine entsprechende Erklärung vorliegt.
                </p>
                <p>
                  Das Hotel haftet für seine vertraglichen Pflichten. Die Haftung für Schäden ausserhalb
                  der typischen Vertragsleistung ist auf Vorsatz und grobe Fahrlässigkeit beschränkt.
                </p>
                <p>
                  Ansprüche des Gastes verjähren nach sechs Monaten. Diese Haftungsbeschränkungen und
                  Verjährungsfristen gelten auch für vorvertragliche Pflichten und Pflichtverletzungen.
                </p>
              </div>
            </div>

            {/* 3. Services, Prices, Payment */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <FileText className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  3. Leistungen, Preise, Zahlung, Aufrechnung
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>
                  Das Hotel ist verpflichtet, die vom Gast gebuchten Zimmer bereitzustellen und die
                  vereinbarten Leistungen zu erbringen.
                </p>
                <p>
                  Der Gast ist verpflichtet, die vereinbarten Preise für die Unterkunft und etwaige
                  zusätzlich in Anspruch genommene Leistungen zu zahlen. Die Preise enthalten die
                  gesetzliche Mehrwertsteuer.
                </p>
                <p>
                  Preise können angepasst werden, wenn der Gast nachträglich Änderungen an Zimmerzahl,
                  Leistungen oder Aufenthaltsdauer wünscht und das Hotel zustimmt.
                </p>
                <p>
                  Hotelrechnungen sind bei Erhalt sofort ohne Abzug fällig. Das Hotel kann ausstehende
                  Beträge jederzeit fällig stellen und sofortige Zahlung verlangen. Bei Zahlungsverzug
                  können Verzugszinsen erhoben werden.
                </p>
                <p>
                  Das Hotel kann bei Vertragsschluss oder danach eine angemessene Vorauszahlung oder
                  Sicherheitsleistung verlangen. Der Gast kann nur mit unbestrittenen oder rechtskräftig
                  festgestellten Forderungen aufrechnen.
                </p>
              </div>
            </div>

            {/* 4. Cancellation by Guest */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <XCircle className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  4. Stornierung durch den Gast
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>
                  Eine Stornierung bedarf der schriftlichen Zustimmung des Hotels. Wird diese nicht
                  erteilt, bleibt der vereinbarte Preis auch bei Nichtinanspruchnahme der Leistung zahlbar.
                </p>
                <p>
                  Wurde schriftlich eine Stornierungsfrist vereinbart, kann der Gast bis zu diesem
                  Zeitpunkt kostenfrei zurücktreten. Das Hotel rechnet Einnahmen aus einer Weitervermietung
                  und ersparte Aufwendungen an.
                </p>
                <p>
                  <strong className="text-foreground">Direktbuchungen</strong> sind vollständig
                  erstattungsfähig, wenn sie bis zu{" "}
                  <strong className="text-foreground">3 Tage vor Anreise bis 18:00 Uhr</strong> storniert werden.
                </p>
                <p>
                  Stornierungen nach Ablauf der kostenlosen Stornierungsfrist sowie Nichterscheinen
                  (No-Show) führen zur Berechnung von{" "}
                  <strong className="text-foreground">100 % des Buchungsbetrags</strong>. Eine Erstattung
                  erfolgt nicht.
                </p>
              </div>
            </div>

            {/* 5. Cancellation by Hotel */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Building className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  5. Stornierung durch das Hotel
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>
                  Hat der Gast ein vertragliches Rücktrittsrecht, kann auch das Hotel zurücktreten,
                  wenn weitere Buchungsanfragen vorliegen und der Gast auf sein Recht nicht verzichtet.
                </p>
                <p>
                  Die Nichtzahlung einer vereinbarten Vorauszahlung berechtigt das Hotel zum Rücktritt.
                </p>
                <p>
                  Das Hotel kann aus berechtigten Gründen zurücktreten, darunter höhere Gewalt,
                  irreführende Angaben oder Gefährdung des Hotelbetriebs oder -rufs. Der Gast wird
                  unverzüglich informiert. Aus einem berechtigten Rücktritt entstehen keine
                  Entschädigungsansprüche.
                </p>
              </div>
            </div>

            {/* 6. Group Bookings */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Users className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  6. Gruppenreservierungen
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>
                  Gruppenbuchungen (mehr als 2 Zimmer) können bis zu{" "}
                  <strong className="text-foreground">7 Tage vor Anreise</strong> kostenlos storniert werden.
                </p>
                <p>
                  Nach diesem Zeitraum kann der volle Buchungsbetrag berechnet werden, sofern nicht
                  schriftlich anders vereinbart.
                </p>
              </div>
            </div>

            {/* 7. Room Availability */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <DoorOpen className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  7. Zimmerverfügbarkeit, Check-in und Check-out
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>Es besteht kein Anspruch auf bestimmte Zimmer.</p>
                <div className="bg-card p-5 rounded-lg space-y-2">
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-muted-foreground">Check-in</span>
                    <span className="text-foreground font-medium">ab 16:00 Uhr</span>
                  </div>
                  <div className="w-full h-px bg-border" />
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-muted-foreground">Check-out</span>
                    <span className="text-foreground font-medium">bis 11:00 Uhr</span>
                  </div>
                  <div className="w-full h-px bg-border" />
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-muted-foreground">Später Check-out bis 18:00</span>
                    <span className="text-foreground font-medium">50 % Zimmerpreis</span>
                  </div>
                  <div className="w-full h-px bg-border" />
                  <div className="flex justify-between font-body text-sm">
                    <span className="text-muted-foreground">Später Check-out nach 18:00</span>
                    <span className="text-foreground font-medium">100 % Zimmerpreis</span>
                  </div>
                </div>
              </div>
            </div>

            {/* 8. Hotel Liability */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <ShieldCheck className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  8. Haftung des Hotels
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>Das Hotel haftet mit der Sorgfalt eines ordentlichen Kaufmanns.</p>
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>
                    Haftung für Gästeeigentum ist gemäss Schweizer Recht (OR 487) auf{" "}
                    <strong className="text-foreground">CHF 1'000</strong> begrenzt.
                  </li>
                  <li>
                    Keine Haftung für auf dem Hotelgelände abgestellte Fahrzeuge, ausser bei
                    Vorsatz oder grober Fahrlässigkeit.
                  </li>
                  <li>
                    Weckrufe werden mit Sorgfalt ausgeführt; Haftung nur bei Vorsatz oder
                    grober Fahrlässigkeit.
                  </li>
                  <li>
                    Post und Pakete werden mit Sorgfalt behandelt; die Haftung ist
                    entsprechend beschränkt.
                  </li>
                </ul>
              </div>
            </div>

            {/* 9. Final Provisions */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Scale className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  9. Schlussbestimmungen
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <ul className="list-disc list-inside space-y-2 ml-4">
                  <li>Änderungen bedürfen der Schriftform.</li>
                  <li>Erfüllungs- und Zahlungsort ist der Sitz des Hotels.</li>
                  <li>Ausschliesslicher Gerichtsstand ist der Sitz des Hotels.</li>
                  <li>Es gilt Schweizer Recht.</li>
                  <li>Unwirksame Bestimmungen berühren die Gültigkeit der übrigen Bedingungen nicht.</li>
                </ul>
              </div>
            </div>

            {/* Vouchers */}
            <div className="bg-background p-8 rounded-lg shadow-soft">
              <div className="flex items-center gap-3 mb-6">
                <div className="p-2 bg-accent/10 rounded-lg shrink-0">
                  <Gift className="w-5 h-5 text-accent" />
                </div>
                <h3 className="font-display text-2xl font-semibold text-foreground">
                  Gutscheine
                </h3>
              </div>
              <div className="space-y-4 font-body text-muted-foreground leading-relaxed">
                <p>
                  Alle bei Esos Hotel Quelle erworbenen Gutscheine sind ausschliesslich in unserem
                  Hotel einlösbar.
                </p>
                <p>
                  Pizol- und Tamina-Therme-Gutscheine sind{" "}
                  <strong className="text-foreground">ein Jahr</strong> gültig.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default ImpressumPage;
